/**
 * Form validation utilities.
 *
 * Centralizes validation logic to keep components focused on presentation.
 * Each rule is testable in isolation and reusable across forms.
 */

/**
 * Password strength rules with test functions.
 * Each rule has an id (for React keys), a user-facing label,
 * and a test function that returns true if the password passes.
 */
export const PASSWORD_RULES = [
  {
    id: 'minLength',
    label: 'At least 8 characters',
    test: (password) => password.length >= 8,
  },
  {
    id: 'uppercase',
    label: 'One uppercase letter',
    test: (password) => /[A-Z]/.test(password),
  },
  {
    id: 'number',
    label: 'One number',
    test: (password) => /\d/.test(password),
  },
  {
    id: 'special',
    label: 'One special character',
    test: (password) => /[!@#$%^&*()_+\-=[\]{}|;:'",.<>?/\\]/.test(password),
  },
];

/**
 * Checks if a password meets all strength requirements.
 *
 * @param {string} password - The password to validate
 * @returns {boolean} True if all rules pass
 */
export const isPasswordValid = (password) =>
  PASSWORD_RULES.every((rule) => rule.test(password));

/**
 * Checks if two passwords match.
 *
 * @param {string} password - The original password
 * @param {string} confirmPassword - The confirmation password
 * @returns {boolean} True if passwords match
 */
export const doPasswordsMatch = (password, confirmPassword) =>
  password === confirmPassword && password.length > 0;

/**
 * Strips non-numeric characters from a string.
 * Used for DOB fields to ensure only numbers are entered.
 *
 * @param {string} value - Input value to sanitize
 * @param {number} maxLength - Maximum allowed length
 * @returns {string} Sanitized numeric string
 */
export const sanitizeNumericInput = (value, maxLength) =>
  value.replace(/\D/g, '').slice(0, maxLength);

/**
 * Basic email format validation.
 *
 * @param {string} email - Email to validate
 * @returns {boolean} True if email format is valid
 */
export const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/**
 * Gets the email validation error message based on current input.
 * Returns null if the email is valid or empty.
 *
 * @param {string} email - Email to validate
 * @returns {string|null} Error message or null if valid
 */
export const getEmailError = (email) => {
  if (!email.trim()) {
    return null; // Don't show error for empty field (let required validation handle it)
  }
  if (!isValidEmail(email)) {
    return 'Please enter a valid email address';
  }
  return null;
};
